/**
 * «Mi perfil» — la misma pantalla para los tres shells.
 *
 * Antes esto era «Mi foto»: un modal que solo subía o quitaba la imagen y que
 * ocupaba un tercio del menú personal. La foto es **un** dato del perfil, no
 * el perfil, y el lugar donde uno espera cambiarse el nombre o la contraseña
 * es el mismo donde se cambia la foto.
 *
 * Es pantalla completa y no modal (decisión del dueño, CB-21): va a crecer
 * —notificaciones, preferencias— y un modal que crece termina con scroll
 * propio adentro de la pantalla, que es el peor de los dos mundos.
 *
 * ── Qué edita cada quien (decisión del dueño, P6.4) ─────────────────────────
 *
 *   nombre, teléfono, foto   uno mismo, acá.
 *   correo, rol              solo un administrador.
 *
 * El correo **es la credencial de ingreso**. Un cliente que se lo cambia mal
 * —un dedo de más, un dominio equivocado— se deja afuera de su propia cuenta
 * y no le queda ni el flujo de restablecer, que también va por correo. Se
 * muestra, se explica a quién pedirle el cambio, y no se edita. La regla vive
 * en el servidor (`routes/me.ts` no escribe `email` aunque venga en el
 * cuerpo); acá solo se cuenta.
 *
 * Una sola pantalla para los tres roles y no tres copias: el admin, el
 * personal y el cliente tienen exactamente el mismo perfil de acceso, y tres
 * copias serían tres lugares donde arreglar el mismo error.
 */

import { useEffect, useState } from 'react';
import { APP_ROLE_LABEL, passwordRule, type AppRole } from '@cleanblue/shared';
import { api } from '@/lib/api';
import { useApi, useApiMutation } from '@/lib/hooks';
import { useSession } from '@/lib/session';
import { Async, Button, Card, Field, Note, PageHead, PasswordInput } from '@/components/ui';
import { PhotoUploader } from '@/components/common/PhotoUploader';
import { Icon } from '@/components/common/NavIcons';
import { activarPush, desactivarPush, estadoPush, type EstadoPush } from '@/lib/push';

interface MyProfileData {
  full_name: string;
  email: string | null;
  phone: string | null;
  photo_key: string | null;
  app_role: AppRole;
}

/** El perfil en el panel de administración: dentro de `.content`, que ya trae su propio margen. */
export function MyProfilePage() {
  return <MyProfile />;
}

/** El mismo perfil en los dos portales móviles, con el margen de `.msec`. */
export function MyProfileMobilePage() {
  return (
    <section className="msec">
      <MyProfile />
    </section>
  );
}

function MyProfile() {
  return (
    <>
      <PageHead
        title="Mi perfil"
        subtitle="Tus datos de acceso. Lo del negocio se configura en Configuración."
      />
      <TarjetasDeCuenta />
    </>
  );
}

/**
 * Las tarjetas de la cuenta, sin el encabezado de pantalla.
 *
 * Sueltas del `PageHead` para que el portal del cliente las pueda montar dentro
 * de una solapa: ahí «Mi cuenta» es una de tres, y un título de pantalla
 * adentro de una solapa es un título de más.
 */
export function TarjetasDeCuenta() {
  const query = useApi<{ profile: MyProfileData }>('/me/profile');
  return (
    <Async query={query}>
      {(data) => (
        <div className="profile-page">
          <IdentityCard profile={data.profile} />
          <AvisosCard />
          <PasswordCard />
        </div>
      )}
    </Async>
  );
}

function IdentityCard({ profile }: { profile: MyProfileData }) {
  const { refresh } = useSession();
  const [fullName, setFullName] = useState(profile.full_name);
  const [phone, setPhone] = useState(profile.phone ?? '');

  // Si el servidor cambia por debajo —otra pestaña, o el propio guardado— el
  // formulario sigue lo que hay en la base, no lo que quedó en memoria.
  useEffect(() => {
    setFullName(profile.full_name);
    setPhone(profile.phone ?? '');
  }, [profile.full_name, profile.phone]);

  const save = useApiMutation(
    () => api.patch('/me/profile', {
      full_name: fullName.trim(),
      // `null` explícito para poder borrarlo: `undefined` significaría «no lo toques».
      phone: phone.trim() || null,
    }),
    ['/me/profile'],
    // La sesión guarda el nombre y la foto para pintarlos en la barra
    // superior: sin este refresco, uno se cambia el nombre y sigue viendo el
    // anterior arriba a la derecha hasta recargar la página.
    { onSuccess: () => void refresh() },
  );

  const dirty = fullName.trim() !== profile.full_name || phone.trim() !== (profile.phone ?? '');

  return (
    <Card
      title="Datos personales"
      actions={
        <Button
          variant="primary"
          disabled={!fullName.trim() || !dirty || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Guardando…' : 'Guardar'}
        </Button>
      }
    >
      {save.error && <Note tone="danger">{save.error.message}</Note>}
      {save.isSuccess && !dirty && <Note tone="ok">Guardado.</Note>}

      <Field label="Foto">
        <PhotoUploader
          uploadPath="/me/photo"
          deletePath="/me/photo"
          photoSrc={profile.photo_key ? `/api/me/photo?v=${profile.photo_key}` : null}
          name={profile.full_name}
          invalidate={['/me/profile']}
          onChanged={() => void refresh()}
          size={72}
        />
      </Field>

      <Field label="Nombre completo" required>
        <input
          className="input"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          autoComplete="name"
        />
      </Field>

      <Field label="Teléfono" hint="Para que administración te ubique rápido.">
        <input
          className="input"
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
        />
      </Field>

      <div className="grid grid--2">
        <Field
          label="Correo"
          hint="Es tu usuario para entrar. Para cambiarlo, pedíselo a un administrador."
        >
          {/* `readOnly` y no `disabled`: un campo deshabilitado no se puede
              seleccionar ni copiar, y esto es justo el dato que uno quiere
              copiar para dictárselo a alguien. */}
          <input className="input" value={profile.email ?? '—'} readOnly />
        </Field>
        <Field label="Rol" hint="Lo asigna un administrador.">
          <input className="input" value={APP_ROLE_LABEL[profile.app_role] ?? profile.app_role} readOnly />
        </Field>
      </div>
    </Card>
  );
}

/**
 * Cambio de contraseña dentro de la sesión (decisión del dueño, P6.5).
 *
 * **Exige la contraseña actual.** No es burocracia: una sesión abierta en una
 * computadora prestada alcanzaría para que cualquiera se apropie de la cuenta
 * cambiando la clave. El servidor la verifica contra el hash existente
 * (`auth/session.ts changePassword` → PBKDF2-SHA256, el mismo algoritmo de
 * siempre) y cierra las demás sesiones al cambiarla.
 *
 * El flujo de restablecer por correo sigue existiendo para quien no puede
 * entrar; este es para quien ya entró.
 */
function PasswordCard() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');

  const rule = passwordRule.safeParse(next);
  const mismatch = confirm.length > 0 && confirm !== next;

  const mutation = useApiMutation(
    () => api.post('/auth/change-password', { current_password: current, new_password: next }),
    [],
    { onSuccess: () => { setCurrent(''); setNext(''); setConfirm(''); } },
  );

  /* Plegada por defecto (dueño, 8-set-2026: «solo colapsaría la parte de
     contraseña, que sea desplegable, para que sea más corta la pantalla»).
     Tiene sentido más allá del alto: cambiar la contraseña es lo que uno hace
     una vez cada tanto, mientras que el teléfono o la foto se corrigen seguido,
     y desplegado empujaba todo eso fuera de la primera pantalla.
     `<details>` nativo y no un estado propio: se abre solo cuando el navegador
     busca texto adentro (Ctrl+F, «buscar en la página» del teléfono), cosa que
     un `useState` no hace y por la que la gente jura que el texto no está. */
  return (
    <Card>
      <details className="plegable">
        <summary className="plegable__cabeza">
          <span className="strong">Contraseña</span>
          <span className="small muted">Cambiala cuando querás</span>
          <Icon name="chevron-der" size={18} />
        </summary>

        <div className="plegable__cuerpo">
          {mutation.error && <Note tone="danger">{mutation.error.message}</Note>}
      {mutation.isSuccess && <Note tone="ok">Contraseña cambiada.</Note>}

      <Note tone="info">
        Al cambiarla se cierran las sesiones abiertas en otros dispositivos. Esta se mantiene.
      </Note>

      <div className="mt-2">
        <Field label="Contraseña actual" required>
          <PasswordInput
            autoComplete="current-password"
            value={current} onChange={setCurrent}
          />
        </Field>
        <div className="grid grid--2">
          <Field
            label="Contraseña nueva"
            required
            hint="Mínimo 8 caracteres."
            error={next.length > 0 && !rule.success ? rule.error?.issues[0]?.message : undefined}
          >
            <PasswordInput
              autoComplete="new-password"
              value={next} onChange={setNext}
            />
          </Field>
          <Field
            label="Repetila"
            required
            error={mismatch ? 'Las dos contraseñas no coinciden.' : undefined}
          >
            <PasswordInput
              autoComplete="new-password"
              value={confirm} onChange={setConfirm}
            />
          </Field>
        </div>
      </div>

          <div className="row mt-2">
            <span className="spacer" />
            <Button
              variant="primary"
              disabled={!current || !rule.success || mismatch || !confirm || mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {mutation.isPending ? 'Cambiando…' : 'Cambiar contraseña'}
            </Button>
          </div>
        </div>
      </details>
    </Card>
  );
}

/**
 * Los avisos nativos del aparato.
 *
 * Vive en «Mi perfil» y no en Configuración porque es una preferencia de LA
 * PERSONA EN ESTE APARATO, no del negocio: la misma cuenta puede tenerlos
 * encendidos en el teléfono y apagados en la computadora, y esta pantalla es la
 * única que sirve igual a las tres superficies.
 *
 * El texto de cada estado dice QUÉ HACER. «No soportado» no le sirve a nadie, y
 * en iPhone además sería falso: lo que pasa es que Safari solo entrega avisos a
 * la aplicación agregada a la pantalla de inicio, así que la respuesta correcta
 * es explicar cómo instalarla.
 */
function AvisosCard() {
  const [estado, setEstado] = useState<EstadoPush | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { void estadoPush().then(setEstado); }, []);

  async function conAviso(fn: () => Promise<unknown>) {
    setOcupado(true); setError(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo');
    } finally {
      setEstado(await estadoPush());
      setOcupado(false);
    }
  }

  return (
    <Card title="Notificaciones">
      {estado === null && <p className="small muted">Revisando…</p>}

      {estado === 'falta-instalar' && (
        <Note tone="info">
          En iPhone las notificaciones llegan solo si abrís Clean Blue desde el ícono de la
          pantalla de inicio. Tocá <b>Compartir</b> y después <b>Agregar a inicio</b>;
          desde ahí vas a poder activarlas.
        </Note>
      )}

      {estado === 'sin-soporte' && (
        <Note tone="info">Este navegador no puede mostrar notificaciones. Probá desde el teléfono.</Note>
      )}

      {estado === 'sin-configurar' && (
        <Note tone="warn">Las notificaciones todavía no están configuradas en el servidor.</Note>
      )}

      {estado === 'bloqueado' && (
        <Note tone="warn">
          Las notificaciones están bloqueadas para Clean Blue. Se vuelven a permitir desde los
          ajustes del navegador o del sistema; desde acá no se puede volver a preguntar.
        </Note>
      )}

      {estado === 'sin-permiso' && (
        <>
          <p className="small muted">
            Te avisamos cuando entre una solicitud, aunque no tengás la aplicación abierta.
          </p>
          <Button variant="primary" disabled={ocupado} onClick={() => void conAviso(activarPush)}>
            {ocupado ? 'Activando…' : 'Activar notificaciones'}
          </Button>
        </>
      )}

      {estado === 'activo' && (
        <>
          <Note tone="ok">Las notificaciones están activadas en este dispositivo.</Note>
          {/* Hubo acá un botón de «mandarme uno de prueba». Lo sacó el dueño el
              8-set-2026, después de validar el canal contra su iPhone: una vez
              que se sabe que funciona, es un botón que no vuelve a apretar
              nadie. El endpoint sigue existiendo para diagnosticar. */}
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <Button disabled={ocupado} onClick={() => void conAviso(desactivarPush)}>
              Desactivar
            </Button>
          </div>
        </>
      )}

      {error && <Note tone="danger">{error}</Note>}
    </Card>
  );
}
